import mLib from './js/mapbox/mapbox';
import { pictograms } from './js/pictograms';
import { coordinatesAreEqual } from './js/tools';

import './css/map.scss';
//TODO : translate map

// ==================================================================================================
// Documentation
// ==================================================================================================

/* markersLists = [{
    type: string,
    link?: boolean,
    markers: [{
        center: { lng: number, lat: number },
        elevation?: number,
        type: string,
        bbox?: [{ lng: number, lat: number }],
        custom?: {
            isPin?: string,
            pinColor?: string,
            icon?: string
        }
    }]
}] */

/* cameraPosition = {
    center: { lng: number, lat: number },
    zoom: number,
    pitch: number,
    bearing: number
} */

/* displayOptions = {
    layer: string,
    displayElevation: boolean,
} */

/* mapOptions = {
    interactive: boolean,
    editable: boolean,
    geocoder: boolean,
    printable: boolean,
    token: string,
    publicUrl: string,
    lists: {
        [type: string]: {
            link: boolean,
            label: string,
            markers: [{
                [type: string]: {
                    isPin?: boolean,
                    pinColor?: string,
                    label: string,
                    icon: string,
                    clusterPriority?: number,
                    default?: boolean,
                    first?: boolean,
                    last?: boolean
                }
            }]
        }
    }
} */

// ==================================================================================================
// Load & Display Map
// ==================================================================================================

function defaultMapOptions(
  containerId,
  {
    interactive = false,
    editable = false,
    geocoder = false,
    printable = false,
    ...otherOptions
  }
) {
  if (!interactive) {
    editable = false;
  }
  if (!editable) {
    geocoder = false;
  }
  const scroll = {
    displayMsg: () =>
      $(`#${containerId} .tp-table-map-scroll`).removeClass('hidden'),
    hideMsg: () => $(`#${containerId} .tp-table-map-scroll`).addClass('hidden'),
  };
  const clusters = {
    priority: getClustersPriority(otherOptions.lists),
  };

  return {
    interactive,
    editable,
    geocoder,
    printable,
    scroll,
    pictograms,
    clusters,
    ...otherOptions,
  };
}

function defaultMarkersLists(markersLists = [], mapOptions) {
  if (markersLists.length === 0) {
    return Object.keys(mapOptions.lists).map((listType) => ({
      type: listType,
      markers: [],
    }));
  } else {
    return markersLists;
  }
}

function defaultDislayOptions({ layer = 'streets', displayElevation = false }) {
  return { layer, displayElevation };
}

export default async function buildMap(
  containerId,
  mapOptions = {},
  { markersLists, cameraPosition, displayOptions = {} },
  mapUpdated
) {
  mapOptions = defaultMapOptions(containerId, mapOptions);
  markersLists = defaultMarkersLists(markersLists, mapOptions);
  displayOptions = defaultDislayOptions(displayOptions);

  const builder = {
    // Map
    map: mLib.create(containerId, mapOptions, displayOptions),
    // Map properties
    mapOptions,
    markersLists,
    cameraPosition,
    displayOptions,
    currentList: 0,
    // Interactivity
    list: function () {
      return this.markersLists[this.currentList];
    },
    options: function () {
      return this.mapOptions.lists[this.list().type];
    },
    markers: function () {
      return [...this.list().markers];
    },
    setMarkers: function (newMarkers) {
      this.list().markers = newMarkers;
      this.update();
    },
    setListLink: function (isLinked) {
      this.list().link = isLinked;
      this.update();
    },
    setCameraPosition: function (position) {
      this.cameraPosition = position;
      this.update();
    },
    setLayer: function (newLayer) {
      this.displayOptions.layer = newLayer;
      this.update();
    },
    setDisplayElevation: function (isDisplayed) {
      this.displayOptions.displayElevation = isDisplayed;
      this.update();
    },
    update: function () {
      if (mapUpdated) {
        mapUpdated(this.markersLists, this.cameraPosition, this.displayOptions);
      }
    },
    markerTypesMgr: markerTypesManagerSingleton(),
  };

  if (mapOptions.geocoder) {
    addGeocoder(builder);
  }

  const allMarkers = getAllMarkers(builder);
  const allLines = getAllLines(builder);
  jumpTo(builder, allMarkers);

  await mLib.isCreated(builder.map);
  await mLib.load(builder.map, allMarkers, allLines);

  if (mapOptions.editable) {
    editableMarkers(builder);
  }

  await mLib.isRendered(builder.map);

  if (mapOptions.editable) {
    const setNewLayer = (newLayer) => {
      builder.setLayer(newLayer);
      mLib.isRendered(builder.map).then(() => {
        mLib.load(builder.map, getAllMarkers(builder), getAllLines(builder));
      });
    };
    mLib.manageLayer(builder.map, builder.displayOptions.layer, setNewLayer);

    if (!builder.options().link) {
      const manageListLink = (isLinked) => {
        builder.setListLink(isLinked);
        if (isLinked) {
          drawLine(builder);
        } else {
          removeLine(builder);
        }
      };
      mLib.manageLinkMarkers(
        builder.map,
        linkListMarkers(builder),
        manageListLink
      );
    }

    const setNewPosition = (newPosition) => {
      builder.setCameraPosition(newPosition);
    };
    mLib.manageCameraPosition(
      builder.map,
      builder.cameraPosition,
      setNewPosition
    );

    const setNewDisplayElevation = (isDisplayed) => {
      builder.setDisplayElevation(isDisplayed);
    };
    mLib.manageDisplayElevation(
      builder.map,
      builder.displayOptions.displayElevation,
      setNewDisplayElevation
    );
  }

  return {
    map: builder.map,
    getCanvas: () => mLib.getCanvas(builder.map),
    remove: () => mLib.remove(builder.map),
    setCurrentList: function (position) {
      builder.currentList = position;
      return this;
    },
  };
}

function getAllMarkers(builder) {
  return builder.markersLists.reduce((allMarkers, list) => {
    const listMarkers = list.markers.map((marker) => ({
      center: marker.center,
      bbox: marker.bbox,
      type: getMarkerType(builder, marker, list),
    }));

    return [...allMarkers, ...listMarkers];
  }, []);
}

function getAllLines(builder) {
  return builder.markersLists
    .filter((list) => list.link ?? builder.mapOptions.lists[list.type].link)
    .map((list, index) => ({
      id: index,
      coordinates: list.markers.map((marker) => marker.center),
    }));
}

// ==================================================================================================
// Camera Position
// ==================================================================================================

function jumpTo(builder, markers) {
  const animate = !builder.mapOptions.printable;

  if (builder.cameraPosition) {
    const { center, zoom, pitch, bearing } = builder.cameraPosition;
    mLib.center(builder.map, animate, center, zoom, pitch, bearing);
  } else {
    if (markers.length === 0) {
      mLib.center(builder.map, animate, { lng: 12, lat: 33 }, 1);
    } else {
      const bbox = markers.reduce((bbox, marker) => {
        if (marker.bbox) {
          return [...bbox, ...marker.bbox];
        } else {
          return [...bbox, marker.center];
        }
      }, []);

      if (bbox.length === 1) {
        mLib.center(builder.map, animate, bbox[0], 16);
      } else {
        mLib.fitBounds(builder.map, animate, bbox);
      }
    }
  }
}

// ==================================================================================================
// Geocoder
// ==================================================================================================

function addGeocoder(builder) {
  mLib.addGeocoder(builder.map, {
    resultFound: ({ center, bbox }) => {
      clickOnMap(builder, center, bbox);
    },
  });
}

// ==================================================================================================
// Clusters
// ==================================================================================================

function getClustersPriority(lists) {
  return Object.keys(lists)
    .reduce((allMarkers, listKey) => {
      const list = lists[listKey];

      const markers = Object.keys(list.markers)
        .filter((key) => list.markers[key].isPin)
        .map((key) => {
          const marker = list.markers[key];
          return {
            type: `${listKey}_${key}`,
            priority: marker.clusterPriority ? marker.clusterPriority : 0,
            pinColor: marker.pinColor,
            icon: marker.icon,
          };
        });

      return [...allMarkers, ...markers];
    }, [])
    .sort((a, b) => b.priority - a.priority);
}

// ==================================================================================================
// Map Interactivity
// ==================================================================================================

function editableMarkers(builder) {
  mLib.editableMarkers(builder.map, {
    clickOnMap: (coordinates) => {
      clickOnMap(builder, coordinates);
    },
    clickOnMarker: (center) => {
      clickOnMarker(builder, center);
    },
    rightClickOnMarker: (center, properties) => {
      rightClickOnMarker(builder, center, properties);
    },
  });
}

function linkListMarkers(builder) {
  return builder.list().link ?? builder.options().link;
}

function clickOnMap(builder, coordinates, bbox) {
  const { defaultType, firstType, lastType } = builder.markerTypesMgr();

  if (linkListMarkers(builder)) {
    const markers = builder.markers();

    switch (markers.length) {
      case 0:
        addMarker(builder, coordinates, firstType, bbox);
        break;
      case 1:
        addMarker(builder, coordinates, lastType, bbox);
        break;
      default: {
        const lastMarker = getLastMarker(builder);
        updateMarker(builder, lastMarker.center, { type: defaultType });
        addMarker(builder, coordinates, lastType, bbox);
      }
    }

    drawLine(builder);
  } else {
    addMarker(builder, coordinates, defaultType, bbox);
  }
}

function clickOnMarker(builder, center) {
  removeMarker(builder, center);

  if (linkListMarkers(builder)) {
    const { firstType, lastType } = builder.markerTypesMgr();

    if (builder.markers().length > 0) {
      const firstMarker = getFirstMarker(builder);
      if (firstMarker.type !== firstType) {
        updateMarker(builder, firstMarker.center, { type: firstType });
      }
    }

    if (builder.markers().length > 1) {
      const lastMarker = getLastMarker(builder);
      if (lastMarker.type !== lastType) {
        updateMarker(builder, lastMarker.center, { type: lastType });
      }
    }

    drawLine(builder);
  }
}

function rightClickOnMarker(builder, center, { pictogram }) {
  const custom = {};
  if (pictogram.isPin) {
    if (pictogram.code === 'noPin') {
      custom.isPin = false;
    } else {
      custom.isPin = true;
      custom.pinColor = pictogram.code;
    }
  } else {
    custom.icon = pictogram.code;
  }
  updateMarker(builder, center, { custom });
}

// ==================================================================================================
// Update Markers & Lines
// ==================================================================================================

function addMarker(builder, center, type, bbox) {
  const rawMarker = { center, type };
  if (bbox) {
    rawMarker.bbox = bbox;
  }
  rawMarker.elevation = mLib.getMarkerElevation(builder.map, center);

  const newMarker = roundMarkerCoordinates(rawMarker);
  builder.setMarkers([...builder.markers(), newMarker]);

  const markerType = getMarkerType(builder, newMarker);
  mLib.addMarker(builder.map, newMarker.center, markerType);
}

function removeMarker(builder, center) {
  const newMarkers = builder.markers().filter((marker) => {
    return !coordinatesAreEqual(marker.center, center);
  });
  if (newMarkers.length !== builder.markers().length) {
    builder.setMarkers(newMarkers);
    mLib.removeMarker(builder.map, center);
  }
}

function updateMarker(builder, center, newProperties) {
  let updatedMarker;
  const newMarkers = builder.markers().map((marker) => {
    if (coordinatesAreEqual(marker.center, center)) {
      updatedMarker = { ...marker, ...newProperties };
      if (newProperties.custom && marker.custom) {
        updatedMarker.custom = {
          ...marker.custom,
          ...newProperties.custom,
        };
      }
      return updatedMarker;
    }
    return marker;
  });
  builder.setMarkers(newMarkers);

  if (updatedMarker) {
    const markerType = getMarkerType(builder, updatedMarker);
    mLib.removeMarker(builder.map, center);
    mLib.addMarker(builder.map, center, markerType);
  }
}

function drawLine(builder) {
  const lineCoordinates = builder.markers().map((marker) => marker.center);
  mLib.drawLine(builder.map, builder.currentList, lineCoordinates);
}

function removeLine(builder) {
  mLib.removeLine(builder.map, builder.currentList);
}

// ==================================================================================================
// Marker Types Manager
// ==================================================================================================

function markerTypesManagerSingleton() {
  const managers = {};

  return function () {
    const listType = this.list().type;
    const markerTypes = this.options().markers;

    if (managers[listType] === undefined) {
      managers[listType] = getMarkerTypesManager(markerTypes);
    }
    return managers[listType];
  };
}

function getMarkerTypesManager(markerTypes) {
  const typeKeys = Object.keys(markerTypes);

  const getTypeByProperty = (property, defaultValue) => {
    const types = typeKeys.filter((type) => markerTypes[type][property]);
    return types.length > 0 ? types[0] : defaultValue;
  };

  const defaultType = getTypeByProperty('default', typeKeys[0]);

  return {
    defaultType,
    firstType: getTypeByProperty('first', defaultType),
    lastType: getTypeByProperty('last', defaultType),
  };
}

// ==================================================================================================
// Tools
// ==================================================================================================

function getMarkerType(builder, marker, list = undefined) {
  list = list ?? builder.list();
  const listOptions = builder.mapOptions.lists[list.type];

  const type = `${list.type}_${marker.type}`;
  const markerType = listOptions.markers[marker.type];
  return { type, ...markerType, ...marker.custom };
}

function getFirstMarker(builder) {
  return builder.markers()[0];
}

function getLastMarker(builder) {
  return builder.markers().slice(-1)[0];
}

function roundMarkerCoordinates(marker) {
  const newMarker = { ...marker };

  newMarker.center = {
    lng: roundCoordinate(newMarker.center.lng),
    lat: roundCoordinate(newMarker.center.lat),
  };
  if (newMarker.bbox) {
    newMarker.bbox = newMarker.bbox.map((c) => ({
      lng: roundCoordinate(c.lng),
      lat: roundCoordinate(c.lat),
    }));
  }
  newMarker.elevation = Math.floor(newMarker.elevation);

  return newMarker;
}

function roundCoordinate(value) {
  return Number(Math.round(value + 'e7') + 'e-7');
}
